/* eslint-disable react/react-in-jsx-scope */
import { lazy } from 'react';
import {
  ELSCommonUIConstants,
  ELSSecurityRoutes
} from '@els/els-ui-common-react';
import { RoutePath } from './app.constants';

export type RouteConfig = {
  path: RoutePath;
  isPrivate: boolean;
  exact: boolean;
  data: {
    analytics: {
      pageType: string;
      pageName: string;
    };
  };
}

export const getRoutePathByPath = (path: string): RoutePath => {
  return Object.values(RoutePath)
    .sort((a, b) => a.length - b.length) // Sort by length to match longer paths first
    .find((routePath) => path.startsWith(routePath));
};

export const convertPathToName = (path: string): string => {
  // remove leading slash
  return path.replace(/^\//, '');
};

const getDefaultRouteConfig = (path: RoutePath): RouteConfig => {
  return {
    path,
    isPrivate: false,
    exact: false,
    data: {
      analytics: {
        pageType: ELSCommonUIConstants.analytics.pageTypes.genericNav,
        pageName: `nra:${convertPathToName(path)}`
      }
    }
  };
};

export const RouteConfigMap: Record<RoutePath, RouteConfig> = {
  [RoutePath.LOGOUT]: getDefaultRouteConfig(RoutePath.LOGOUT),
  [RoutePath.UNEXPECTED_ERROR]: getDefaultRouteConfig(RoutePath.UNEXPECTED_ERROR),
  [RoutePath.HELP]: getDefaultRouteConfig(RoutePath.HELP),
  [RoutePath.TIMEOUT]: getDefaultRouteConfig(RoutePath.TIMEOUT),
  [RoutePath.NOT_AUTHORIZED]: getDefaultRouteConfig(RoutePath.NOT_AUTHORIZED),
  [RoutePath.PAGE_NOT_FOUND]: getDefaultRouteConfig(RoutePath.PAGE_NOT_FOUND),
  [RoutePath.MAINTENANCE]: getDefaultRouteConfig(RoutePath.MAINTENANCE),
  [RoutePath.LOGIN]: getDefaultRouteConfig(RoutePath.LOGIN),
  [RoutePath.APP_LINK_REDIRECT]: getDefaultRouteConfig(RoutePath.APP_LINK_REDIRECT),
  [RoutePath.SUPPORT]: {
    ...getDefaultRouteConfig(RoutePath.SUPPORT),
    isPrivate: true
  },
  [RoutePath.REMEDIATION_STUDENT_HOME]: {
    ...getDefaultRouteConfig(RoutePath.REMEDIATION_STUDENT_HOME),
    isPrivate: true
  },
  [RoutePath.REMEDIATION_NEXT_RECOMMENDATION]: {
    ...getDefaultRouteConfig(RoutePath.REMEDIATION_NEXT_RECOMMENDATION),
    isPrivate: true
  },
  [RoutePath.REMEDIATION_POST_PLAYER]: {
    ...getDefaultRouteConfig(RoutePath.REMEDIATION_POST_PLAYER),
    isPrivate: true
  },
  [RoutePath.OSMOSIS_PLAYER]: {
    ...getDefaultRouteConfig(RoutePath.OSMOSIS_PLAYER),
    isPrivate: true
  },
  [RoutePath.EBOOK_PLAYER]: {
    ...getDefaultRouteConfig(RoutePath.EBOOK_PLAYER),
    isPrivate: true
  },
  [RoutePath.TOPIC_OVERVIEW_PLAYER]: {
    ...getDefaultRouteConfig(RoutePath.TOPIC_OVERVIEW_PLAYER),
    isPrivate: true
  },
  [RoutePath.REMEDIATION_STUDENT_DETAIL]: {
    ...getDefaultRouteConfig(RoutePath.REMEDIATION_STUDENT_DETAIL),
    isPrivate: true
  },
  [RoutePath.ADMIN]: {
    ...getDefaultRouteConfig(RoutePath.ADMIN),
    isPrivate: true
  },
  [RoutePath.PLP_LIST]: {
    ...getDefaultRouteConfig(RoutePath.PLP_LIST),
    isPrivate: true
  }
};

export type AppRoute = RouteConfig & {
  component: React.LazyExoticComponent<React.ComponentType<unknown>>;
}
export const getAppRoutes = (): AppRoute[] => [
  ...ELSSecurityRoutes.getSecurityRoutes()
    .filter((route) => {
      return route.path !== `/${ELSCommonUIConstants.security.States.Admin}`;
    }),
  {
    ...RouteConfigMap[RoutePath.SUPPORT],
    component: lazy(() => import(/* webpackChunkName: "Support.page" */ '../../pages/support/Support.page')),
  }, {
    ...RouteConfigMap[RoutePath.APP_LINK_REDIRECT],
    component: lazy(() => import(/* webpackChunkName: "AppLinkRedirect.page" */ '../../pages/app-link-redirect/AppLinkRedirect.page')),
  }, {
    ...RouteConfigMap[RoutePath.REMEDIATION_STUDENT_HOME],
    component: lazy(() => import(/* webpackChunkName: "Support.page" */ '../../pages/remediation-home/RemediationStudentHome.page')),
  }, {
    ...RouteConfigMap[RoutePath.REMEDIATION_NEXT_RECOMMENDATION],
    component: lazy(() => import(/* webpackChunkName: "NextRecommendation.page" */ '../../pages/next-recommendation/NextRecommendation.page')),
  }, {
    ...RouteConfigMap[RoutePath.REMEDIATION_POST_PLAYER],
    component: lazy(() => import(/* webpackChunkName: "RemediationPostPlayer.page" */ '../../pages/remediation-post-player/RemediationPostPlayer.page')),
  }, {
    ...RouteConfigMap[RoutePath.OSMOSIS_PLAYER],
    component: lazy(() => import(/* webpackChunkName: "Support.page" */ '../../pages/osmosis-player/OsmosisPlayer.page')),
  }, {
    ...RouteConfigMap[RoutePath.EBOOK_PLAYER],
    component: lazy(() => import(/* webpackChunkName: "Support.page" */ '../../pages/ebook-player/EbookPlayer.page')),
  }, {
    ...RouteConfigMap[RoutePath.TOPIC_OVERVIEW_PLAYER],
    component: lazy(() => import(/* webpackChunkName: "Support.page" */ '../../pages/topic-overview-player/TopicOverviewPlayer.page')),
  }, {
    ...RouteConfigMap[RoutePath.REMEDIATION_STUDENT_DETAIL],
    component: lazy(() => import(/* webpackChunkName: "Support.page" */ '../../pages/remediation-student-activity/RemediationStudentActivity.page')),
  }, {
    ...RouteConfigMap[RoutePath.ADMIN],
    component: lazy(() => import(/* webpackChunkName: "Support.page" */ '../../pages/admin/Admin.page')),
  }, {
    ...RouteConfigMap[RoutePath.PLP_LIST],
    component: lazy(() => import(/* webpackChunkName: "PlpList.page" */ '../../pages/plp-list/PlpList.page')),
  }
];
